import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, CheckCircle2, XCircle, Activity, Database, ShieldAlert, Trash2, Zap } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "medium" });
  } catch {
    return iso;
  }
}

function fmtDuration(sec) {
  if (!sec && sec !== 0) return "—";
  if (sec < 60) return `${sec} s`;
  if (sec < 3600) return `${Math.round(sec / 60)} min`;
  return `${Math.round(sec / 3600)} h`;
}

function Stat({ label, value, sub, icon: Icon, tone = "neutral", testid }) {
  const tones = {
    neutral: "bg-white border-brand-line",
    good: "bg-emerald-50/50 border-emerald-200",
    warn: "bg-amber-50/50 border-amber-200",
    bad: "bg-red-50/50 border-red-200",
  };
  return (
    <div className={`border ${tones[tone]} px-5 py-4`} data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] uppercase tracking-[0.14em] text-neutral-500">{label}</span>
        {Icon && <Icon className="h-4 w-4 text-neutral-400" />}
      </div>
      <div className="text-2xl font-semibold text-neutral-900 leading-none">{value}</div>
      {sub && <div className="text-xs text-neutral-500 mt-1.5">{sub}</div>}
    </div>
  );
}

function endpointLabel(ep) {
  return {
    tile: "Traffic-Kachel",
    geocode: "Geocoding",
    route: "Routing",
    test: "Test-Call",
  }[ep] || ep;
}

function statusBadge(status) {
  const ok = status < 400;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-medium ${ok ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-red-50 text-red-700 border border-red-200"}`}>
      {status}
    </span>
  );
}

function EndpointBreakdown({ data, title }) {
  const rows = Object.entries(data || {});
  const total = rows.reduce((s, [, v]) => s + v.total, 0);
  const billable = rows.reduce((s, [, v]) => s + v.billable, 0);
  const cached = rows.reduce((s, [, v]) => s + v.cached, 0);
  const errors = rows.reduce((s, [, v]) => s + v.errors, 0);
  const cacheHitRate = total ? Math.round((cached / total) * 100) : 0;
  return (
    <div className="border border-brand-line bg-white" data-testid={`tomtom-breakdown-${title.toLowerCase()}`}>
      <div className="px-5 py-3 border-b border-brand-line flex items-baseline justify-between">
        <h3 className="text-sm font-semibold text-neutral-900">{title}</h3>
        <span className="text-xs text-neutral-500">
          {total} Calls · {billable} kostenpflichtig · Cache-Trefferquote {cacheHitRate}%
        </span>
      </div>
      {rows.length === 0 ? (
        <div className="px-5 py-6 text-sm text-neutral-500">Keine Aufrufe in diesem Zeitraum.</div>
      ) : (
        <div className="divide-y divide-brand-line">
          {rows.map(([ep, v]) => (
            <div key={ep} className="grid grid-cols-5 items-center px-5 py-3 text-sm">
              <div className="col-span-2 font-medium text-neutral-800">{endpointLabel(ep)}</div>
              <div className="text-neutral-600">
                <span className="text-neutral-900 font-semibold">{v.total}</span>
                <span className="text-xs ml-1 text-neutral-500">gesamt</span>
              </div>
              <div className="text-neutral-600">
                <span className={`font-semibold ${v.billable === 0 ? "text-emerald-600" : "text-neutral-900"}`}>{v.billable}</span>
                <span className="text-xs ml-1 text-neutral-500">TomTom-Calls</span>
              </div>
              <div className="text-neutral-600 text-right">
                {v.errors > 0 && (
                  <span className="inline-flex items-center gap-1 text-red-600 text-xs mr-2">
                    <ShieldAlert className="h-3 w-3" /> {v.errors}
                  </span>
                )}
                <span className="text-xs text-neutral-500">
                  {v.total ? Math.round((v.cached / v.total) * 100) : 0}% Cache
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
      {errors > 0 && (
        <div className="px-5 py-2 bg-red-50/60 border-t border-red-200 text-xs text-red-700">
          {errors} Fehlerantwort(en) — siehe Aktivitätsprotokoll unten.
        </div>
      )}
    </div>
  );
}

export default function AdminTomTom() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [clearing, setClearing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/tomtom/status");
      setData(r.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Konnte TomTom-Status nicht laden.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const testApi = async () => {
    setTesting(true);
    try {
      const r = await api.post("/tomtom/test");
      toast.success(`API OK · ${r.data.duration_ms} ms · ${r.data.sample?.address || "Antwort erhalten"}`);
      await load();
    } catch (e) {
      toast.error("API-Test fehlgeschlagen: " + (formatApiError(e.response?.data?.detail) || e.message));
      await load();
    } finally {
      setTesting(false);
    }
  };

  const clearCache = async () => {
    if (!window.confirm("Sicher? Der Backend-Cache wird geleert und die nächsten Anfragen kosten TomTom-Credits.")) return;
    setClearing(true);
    try {
      await api.post("/tomtom/cache/clear");
      toast.success("Cache geleert.");
      await load();
    } catch (e) {
      toast.error("Cache leeren fehlgeschlagen.");
    } finally {
      setClearing(false);
    }
  };

  const keyOk = data?.api_key_configured;
  const lastErr = data?.last_error;
  const lastOk = data?.last_ok;

  return (
    <div className="space-y-8" data-testid="admin-tomtom-page">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--brand-green-strong))] mb-2">Kosten &amp; Auslastung</p>
          <h1 className="text-3xl font-semibold text-neutral-900 mb-2">TomTom API</h1>
          <p className="text-sm text-neutral-500 max-w-2xl">
            Alle TomTom-Aufrufe laufen über einen serverseitigen Proxy mit Cache. Diese Übersicht zeigt echte, kostenpflichtige Aufrufe, Cache-Trefferquote und Fehler.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            disabled={loading}
            data-testid="tomtom-reload-btn"
            className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-brand-line hover:bg-neutral-50 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Neu laden
          </button>
          <button
            onClick={testApi}
            disabled={testing || !keyOk}
            data-testid="tomtom-test-btn"
            className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-[hsl(var(--brand-green-strong))] text-white hover:opacity-90 disabled:opacity-50"
          >
            <Zap className="h-4 w-4" /> {testing ? "Prüfe…" : "API jetzt testen"}
          </button>
          <button
            onClick={clearCache}
            disabled={clearing}
            data-testid="tomtom-clear-cache-btn"
            className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-brand-line hover:bg-neutral-50 disabled:opacity-50 text-neutral-700"
          >
            <Trash2 className="h-4 w-4" /> Cache leeren
          </button>
        </div>
      </div>

      {/* API-Key card */}
      <div className={`border ${keyOk ? "border-emerald-200 bg-emerald-50/40" : "border-red-200 bg-red-50/40"} px-6 py-5`} data-testid="tomtom-key-status">
        <div className="flex items-center gap-3">
          {keyOk ? (
            <CheckCircle2 className="h-6 w-6 text-emerald-600 shrink-0" />
          ) : (
            <XCircle className="h-6 w-6 text-red-600 shrink-0" />
          )}
          <div className="flex-1">
            <div className="text-sm font-semibold text-neutral-900">
              {keyOk ? "API-Key konfiguriert" : "Kein API-Key konfiguriert"}
            </div>
            <div className="text-xs text-neutral-600 mt-0.5">
              {keyOk
                ? <>Backend-Key: <span className="font-mono">{data?.api_key_preview}</span> · nur serverseitig sichtbar</>
                : "Bitte TOMTOM_API_KEY in /app/backend/.env setzen und Backend neu starten."}
            </div>
          </div>
        </div>
      </div>

      {/* Overview stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat
          testid="tomtom-stat-today-billable"
          label="Heute · kostenpflichtige Calls"
          value={Object.values(data?.today || {}).reduce((s, v) => s + v.billable, 0)}
          sub={`${Object.values(data?.today || {}).reduce((s, v) => s + v.total, 0)} Gesamt-Requests`}
          icon={Activity}
          tone="good"
        />
        <Stat
          testid="tomtom-stat-month-billable"
          label="Diesen Monat · kostenpflichtig"
          value={Object.values(data?.month || {}).reduce((s, v) => s + v.billable, 0)}
          sub={`${Object.values(data?.month || {}).reduce((s, v) => s + v.total, 0)} Gesamt-Requests`}
          icon={Activity}
        />
        <Stat
          testid="tomtom-stat-cache"
          label="Cache-Einträge"
          value={
            (data?.cache?.tile_entries || 0) +
            (data?.cache?.geocode_entries || 0) +
            (data?.cache?.route_entries || 0)
          }
          sub={`Tiles: ${data?.cache?.tile_entries || 0} · Geocode: ${data?.cache?.geocode_entries || 0} · Route: ${data?.cache?.route_entries || 0}`}
          icon={Database}
        />
        <Stat
          testid="tomtom-stat-errors"
          label="Fehler heute"
          value={Object.values(data?.today || {}).reduce((s, v) => s + v.errors, 0)}
          sub="HTTP ≥ 400 (403 = Kontingent leer)"
          icon={ShieldAlert}
          tone={Object.values(data?.today || {}).reduce((s, v) => s + v.errors, 0) > 0 ? "bad" : "neutral"}
        />
      </div>

      {/* Endpoint breakdown */}
      <div className="grid lg:grid-cols-2 gap-6">
        <EndpointBreakdown data={data?.today} title="Heute" />
        <EndpointBreakdown data={data?.month} title="Dieser Monat" />
      </div>

      {/* Cache TTL config */}
      <div className="border border-brand-line bg-white px-6 py-5" data-testid="tomtom-cache-config">
        <h3 className="text-sm font-semibold text-neutral-900 mb-3">Cache-Konfiguration</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-xs">
          <div><span className="text-neutral-500">Traffic-Kacheln</span><div className="text-lg font-semibold text-neutral-900">{fmtDuration(data?.cache?.tile_ttl_sec)}</div></div>
          <div><span className="text-neutral-500">Geocoding</span><div className="text-lg font-semibold text-neutral-900">{fmtDuration(data?.cache?.geocode_ttl_sec)}</div></div>
          <div><span className="text-neutral-500">Routing</span><div className="text-lg font-semibold text-neutral-900">{fmtDuration(data?.cache?.route_ttl_sec)}</div></div>
          <div><span className="text-neutral-500">Refresh-Sperre</span><div className="text-lg font-semibold text-neutral-900">{fmtDuration(data?.cache?.refresh_rate_limit_sec)}</div></div>
        </div>
      </div>

      {/* Last OK / Error */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="border border-brand-line bg-white" data-testid="tomtom-last-ok">
          <div className="px-5 py-3 border-b border-brand-line flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
            <h3 className="text-sm font-semibold text-neutral-900">Letzter erfolgreicher Call</h3>
          </div>
          <div className="px-5 py-4 text-sm text-neutral-700">
            {lastOk ? (
              <>
                <div className="flex items-center gap-2 mb-1">
                  {statusBadge(lastOk.status)}
                  <span className="font-medium">{endpointLabel(lastOk.endpoint)}</span>
                  <span className="text-xs text-neutral-500">{lastOk.duration_ms} ms</span>
                </div>
                <div className="text-xs text-neutral-500">{fmtDateTime(lastOk.ts)}</div>
              </>
            ) : (
              <span className="text-neutral-500">Noch keine Aufrufe.</span>
            )}
          </div>
        </div>
        <div className="border border-brand-line bg-white" data-testid="tomtom-last-error">
          <div className="px-5 py-3 border-b border-brand-line flex items-center gap-2">
            <ShieldAlert className="h-4 w-4 text-red-600" />
            <h3 className="text-sm font-semibold text-neutral-900">Letzter Fehler</h3>
          </div>
          <div className="px-5 py-4 text-sm text-neutral-700">
            {lastErr ? (
              <>
                <div className="flex items-center gap-2 mb-1">
                  {statusBadge(lastErr.status)}
                  <span className="font-medium">{endpointLabel(lastErr.endpoint)}</span>
                </div>
                <div className="text-xs text-neutral-500 mb-1">{fmtDateTime(lastErr.ts)}</div>
                {lastErr.error && (
                  <div className="text-xs text-red-700 bg-red-50 border border-red-200 px-3 py-2 mt-2 font-mono break-all">
                    {lastErr.error}
                  </div>
                )}
              </>
            ) : (
              <span className="text-neutral-500">Keine Fehler bekannt.</span>
            )}
          </div>
        </div>
      </div>

      {/* Recent activity log */}
      <div className="border border-brand-line bg-white" data-testid="tomtom-recent-log">
        <div className="px-5 py-3 border-b border-brand-line">
          <h3 className="text-sm font-semibold text-neutral-900">Aktivitätsprotokoll (letzte 20)</h3>
        </div>
        {(!data?.recent || data.recent.length === 0) ? (
          <div className="px-5 py-6 text-sm text-neutral-500">Noch keine Einträge.</div>
        ) : (
          <div className="divide-y divide-brand-line text-sm">
            {data.recent.map((r, i) => (
              <div key={i} className="grid grid-cols-12 items-center gap-3 px-5 py-2.5">
                <div className="col-span-3 text-xs text-neutral-500 font-mono">{fmtDateTime(r.ts)}</div>
                <div className="col-span-2 flex items-center gap-2">{statusBadge(r.status)}<span className="text-xs">{endpointLabel(r.endpoint)}</span></div>
                <div className="col-span-2 text-xs">
                  {r.cached ? (
                    <span className="inline-flex items-center px-1.5 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200">CACHE</span>
                  ) : (
                    <span className="inline-flex items-center px-1.5 py-0.5 bg-amber-50 text-amber-700 border border-amber-200">TOMTOM</span>
                  )}
                </div>
                <div className="col-span-2 text-xs text-neutral-500">{r.duration_ms} ms</div>
                <div className="col-span-3 text-xs text-neutral-500 truncate" title={r.error || r.tile || r.address}>
                  {r.error || r.tile || r.address || ""}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
